import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	getUserWallets,
	addWallet,
	deleteWallet,
	editWallet,
	getUserCategories,
	getUserTransactions,
	addCategoryGroup,
	addSubcategory,
	editCategoryGroup,
	editSubcategory,
	deleteSubcategory,
	deleteCategoryGroup,
	getAvailableCurrencies,
	addCashTransaction,
	getWalletCategories,
	editTransaction,
	markTransactionAsConnectedWithAssetOrLiability,
	deleteTransaction,
	getNetWorth,
	generateHistoricalNetWorth,
	getUserBudgets,
	saveBudget,
	getCashflow,
	getTodayNetWorth,
	getWalletValueOnDate,
	sendSupportRequest,
	getUserDetails,
	updateUserSettings,
	verifyEmailChange,
	changeEmailRequest,
} from "./api";

const useQueryMutation = (mutationFn, ...queryKeys) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (params) => {
			const data = await mutationFn(params);
			queryKeys.forEach((key) =>
				queryClient.invalidateQueries({ queryKey: [key] })
			);
			return data;
		},
	});
};

// Wallets queries
export const useWalletsQuery = () =>
	useQuery({
		queryKey: ["wallets"],
		queryFn: getUserWallets,
		staleTime: Infinity,
		cacheTime: Infinity,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

export const useAddWalletMutation = () =>
	useQueryMutation(
		({ name, type, currency, balance, category, createdAt }) =>
			addWallet({ name, type, currency, balance, category, createdAt }),
		"wallets"
	);

export const useDeleteWalletMutation = () =>
	useQueryMutation(({ walletId }) => deleteWallet(walletId), "wallets");

export const useEditWalletMutation = () =>
	useQueryMutation(
		({ walletId, walletName, walletCategory, balance, createdAt }) =>
			editWallet({
				walletId,
				walletName,
				walletCategory,
				balance,
				createdAt,
			}),
		"wallets",
		"transactions"
	);

export const useWalletCategoriesQuery = () =>
	useQuery({
		queryKey: ["walletcategories"],
		queryFn: getWalletCategories,
	});

// Get wallet value on a date
export const useGetWalletValueOnDate = (walletId, date, isOpen) =>
	useQuery({
		queryKey: ["walletValueOnDate", walletId, date],
		queryFn: () => getWalletValueOnDate({ walletId, date }),
		enabled: !!walletId && !!date && isOpen,
	});

// Categories queries
export const useCategoriesQuery = () =>
	useQuery({
		queryKey: ["categories"],
		queryFn: getUserCategories,
		staleTime: 30 * 60 * 1000,
	});

export const useAddCategoryGroupMutation = () =>
	useQueryMutation(
		({ type, groupName, color }) =>
			addCategoryGroup({ type, groupName, color }),
		"categories"
	);

export const useAddSubcategoryMutation = () =>
	useQueryMutation(
		({ categoryGroupId, name, emoji }) =>
			addSubcategory({ categoryGroupId, name, emoji }),
		"categories"
	);

export const useEditCategoryGroupMutation = () =>
	useQueryMutation(
		({ categoryGroupId, groupName, color }) =>
			editCategoryGroup({ categoryGroupId, groupName, color }),
		"categories"
	);

export const useEditSubcategoryMutation = () =>
	useQueryMutation(
		({ categoryGroupId, subcategoryId, name, emoji }) =>
			editSubcategory({ categoryGroupId, subcategoryId, name, emoji }),
		"categories"
	);

export const useDeleteSubcategoryMutation = () =>
	useQueryMutation(
		({ categoryGroupId, subcategoryId, newCategoryId, newSubcategoryId }) =>
			deleteSubcategory({
				categoryGroupId,
				subcategoryId,
				newCategoryId,
				newSubcategoryId,
			}),
		"categories",
		"transactions"
	);

export const useDeleteCategoryGroupMutation = () =>
	useQueryMutation(
		({ categoryGroupId, newCategoryId, newSubcategoryId }) =>
			deleteCategoryGroup({
				categoryGroupId,
				newCategoryId,
				newSubcategoryId,
			}),
		"categories",
		"transactions"
	);

// Transactions queries
export const useTransactionsQuery = (
	startDate,
	endDate,
	type,
	includeInAnalysis,
	enabled = true
) =>
	useQuery({
		queryKey: ["transactions", startDate, endDate, type, includeInAnalysis],
		queryFn: () =>
			getUserTransactions({
				startDate,
				endDate,
				type,
				includeInAnalysis,
			}),
		enabled,
	});

export const useAddTransactionsMutation = () =>
	useQueryMutation(
		({
			walletId,
			type,
			amount,
			currency,
			categoryId,
			subcategoryId,
			description,
			createdAt,
			includeInAnalysis,
		}) =>
			addCashTransaction({
				walletId,
				type,
				amount,
				currency,
				categoryId,
				subcategoryId,
				description,
				createdAt,
				includeInAnalysis,
			}),
		"transactions",
		"wallets"
	);

export const useEditTransactionMutation = () =>
	useQueryMutation(
		({
			transactionId,
			includeInAnalysis,
			description,
			amount,
			currency,
			createdAt,
			walletId,
			categoryId,
			subcategoryId,
			type,
		}) =>
			editTransaction({
				transactionId,
				includeInAnalysis,
				description,
				amount,
				currency,
				createdAt,
				walletId,
				categoryId,
				subcategoryId,
				type,
			}),
		"transactions",
		"wallets"
	);

export const useToggleTransactionIncludeInAnalysisMutation = () =>
	useQueryMutation(
		({ transactionId, includeInAnalysis }) =>
			editTransaction({ transactionId, includeInAnalysis }),
		"transactions"
	);

export const useMarkTransactionAsConnectedWithAssetOrLiabilityMutation = () =>
	useQueryMutation(
		({ transactionId, investmentDebtPayoff }) =>
			markTransactionAsConnectedWithAssetOrLiability({
				transactionId,
				investmentDebtPayoff,
			}),
		"transactions",
		"wallets"
	);

export const useDeleteTransactionMutation = () =>
	useQueryMutation(
		({ transactionId }) => deleteTransaction(transactionId),
		"transactions",
		"wallets"
	);

// Currencies queries
export const useCurrenciesQuery = () =>
	useQuery({
		queryKey: ["currencies"],
		queryFn: getAvailableCurrencies,
		staleTime: Infinity,
		cacheTime: Infinity,
	});

// Net worth queries
export const useNetWorthQuery = (endDate, type) =>
	useQuery({
		queryKey: ["networth", endDate, type],
		queryFn: () => getNetWorth({ endDate, type }),
		enabled: !!endDate,
	});

export const useTodayNetWorthQuery = () =>
	useQuery({
		queryKey: ["todayNetWorth"],
		queryFn: getTodayNetWorth,
	});

export const useGenerateHistoricalNetWorthMutation = () =>
	useQueryMutation(() => generateHistoricalNetWorth(), "networth");

// Budgets queries
export const useBudgetsQuery = (period) =>
	useQuery({
		queryKey: ["budgets", period],
		queryFn: () => getUserBudgets(period),
	});

export const useSaveBudgetMutation = () =>
	useQueryMutation((budget) => saveBudget(budget), "budgets");

// Cashflow queries
export const useCashflowQuery = (period, periodType) =>
	useQuery({
		queryKey: ["cashflow", periodType, period],
		queryFn: () => getCashflow({ period, periodType }),
	});

// Support queries
export const useSupportMutation = () =>
	useMutation({
		mutationFn: ({ topic, description, attachments }) =>
			sendSupportRequest({ topic, description, attachments }),
	});

// User queries

export const useUserQuery = () =>
	useQuery({
		queryKey: ["user"],
		queryFn: getUserDetails,
	});

export const useUpdateUserSettingsMutation = () =>
	useQueryMutation(
		(payload) => updateUserSettings(payload),
		"user",
		"transactions",
		"budgets",
		"networth"
	);

export const useChangeEmailMutation = () =>
	useQueryMutation((payload) => changeEmailRequest(payload), null);

export const useVerifyEmailChangeMutation = () =>
	useQueryMutation((token) => verifyEmailChange(token), "user");
